import { client } from '../..'
import {
  REFRESH_MY_SKILL_STATUS,
  REFRESH_EMPLOYEE_SKILL_STATUS,
  REFRESH_SUBORDINATES_OF_COMPANY_SKILL_STATUSES,
  REFRESH_SUBORDINATES_SKILL_STATUSES,
  REFRESH_EMPLOYEES_OF_COMPANY_SKILL_STATUSES,
  REFRESH_EMPLOYEES_OF_BU_SKILL_STATUSES,
  SEND_SKILL_STATUS_UPDATE_REMINDER_EMAIL,
} from './Mutations'
import { MY_PRIVATE_PROFILE, PUBLIC_PROFILE } from '../profiles/Queries'
import {
  EMPLOYEE_SKILL_STATUS_IN_MEETING,
  PERSONAL_SKILL_STATUS_IN_MEETING_PREPARATION,
  SKILL_STATUS_DATA_IN_MEETING_INVITATION,
} from './Queries'
import { SUBORDINATE_OF_COMPANY } from '../companySettings/CompanySettingsQueries'
import { SUBORDINATES } from '../subordinates/Queries'
import {
  Country,
  RetrieveSkillStatusDataInMeetingInvitationQuery,
  RetrieveSkillStatusDataInMeetingInvitationQueryVariables,
} from '../../types'
import { EMPLOYEES_AND_APPRAISALS, BU_EMPLOYEES_AND_APPRAISALS } from '../employee/Queries'
import { SendSkillStatusUpdateReminderEmailMutation, SendSkillStatusUpdateReminderEmailMutationVariables } from '../../types'
import { ApolloQueryResult, FetchResult } from '@apollo/client'

export const refreshMySkillStatus = async (employeeId: string): Promise<FetchResult> => {
  const mutationResult = await client.mutate({
    mutation: REFRESH_MY_SKILL_STATUS,
    variables: {
      id: employeeId,
    },
  })

  await client.query({
    query: MY_PRIVATE_PROFILE,
    variables: { id: employeeId },
    fetchPolicy: 'network-only',
  })

  return mutationResult
}

export const refreshEmployeeSkillStatus = async (employeeId: string): Promise<FetchResult> => {
  const mutationResult = await client.mutate({
    mutation: REFRESH_EMPLOYEE_SKILL_STATUS,
    variables: {
      id: employeeId,
    },
  })

  await client.query({
    query: PUBLIC_PROFILE,
    variables: { id: employeeId },
    fetchPolicy: 'network-only',
  })

  return mutationResult
}

export const refreshEmployeeSkillStatusInAppraisalInviteModal = async (
  employeeId: string,
): Promise<ApolloQueryResult<RetrieveSkillStatusDataInMeetingInvitationQuery>> => {
  return await client.query<RetrieveSkillStatusDataInMeetingInvitationQuery, RetrieveSkillStatusDataInMeetingInvitationQueryVariables>({
    query: SKILL_STATUS_DATA_IN_MEETING_INVITATION,
    variables: {
      employeeId,
    },
    fetchPolicy: 'network-only',
  })
}

export const refreshMySkillStatusInEmployeePreparation = async (employeeId: string): Promise<FetchResult> => {
  const mutationResult = await client.mutate({
    mutation: REFRESH_MY_SKILL_STATUS,
    variables: {
      id: employeeId,
    },
  })

  await client.query({
    query: PERSONAL_SKILL_STATUS_IN_MEETING_PREPARATION,
    variables: { id: employeeId },
    fetchPolicy: 'network-only',
  })

  return mutationResult
}

export const refreshEmployeeSkillStatusInMeeting = async (employeeId: string): Promise<FetchResult> => {
  const mutationResult = await client.mutate({
    mutation: REFRESH_EMPLOYEE_SKILL_STATUS,
    variables: {
      id: employeeId,
    },
  })

  await client.query({
    query: EMPLOYEE_SKILL_STATUS_IN_MEETING,
    variables: { id: employeeId },
    fetchPolicy: 'network-only',
  })

  return mutationResult
}

// The refetchQueries is not used because it is executed in the background after the mutation,
// so the await only waits for the mutation and not for the refetch of the query.
export const refreshSubordinatesOfCompanySkillStatuses = async (companyId: string): Promise<FetchResult> => {
  const mutationResult = await client.mutate({
    mutation: REFRESH_SUBORDINATES_OF_COMPANY_SKILL_STATUSES,
    variables: {
      companyId: companyId,
    },
  })

  await client.query({
    query: SUBORDINATE_OF_COMPANY,
    variables: { companyId: companyId },
    fetchPolicy: 'network-only',
  })

  return mutationResult
}

// The refetchQueries is not used because it is executed in the background after the mutation,
// so the await only waits for the mutation and not for the refetch of the query.
export const refreshSubordinatesSkillStatuses = async (
  companyId: string,
  reviewer: boolean,
  buId?: string | null,
): Promise<FetchResult> => {
  const variables = {
    companyId: companyId,
    reviewer: reviewer,
    buId: buId,
    country: Country.All,
  }

  const mutationResult = await client.mutate({
    mutation: REFRESH_SUBORDINATES_SKILL_STATUSES,
    variables: {
      companyId: companyId,
      reviewer: reviewer,
      buId: buId,
    },
  })

  await client.query({
    query: SUBORDINATES,
    variables: variables,
    fetchPolicy: 'network-only',
  })

  return mutationResult
}

// The refetchQueries is not used because it is executed in the background after the mutation,
// so the await only waits for the mutation and not for the refetch of the query.
export const refreshEmployeesOfCompanySkillStatuses = async (companyId: string, country: Country): Promise<FetchResult> => {
  const variables = {
    companyId: companyId,
    country: country,
  }

  const mutationResult = await client.mutate({
    mutation: REFRESH_EMPLOYEES_OF_COMPANY_SKILL_STATUSES,
    variables: variables,
  })

  await client.query({
    query: EMPLOYEES_AND_APPRAISALS,
    variables: variables,
    fetchPolicy: 'network-only',
  })

  return mutationResult
}

// The refetchQueries is not used because it is executed in the background after the mutation,
// so the await only waits for the mutation and not for the refetch of the query.
export const refreshEmployeesOfBUSkillStatuses = async (businessUnitId: string): Promise<FetchResult> => {
  const variables = {
    businessUnitId: businessUnitId,
  }

  const mutationResult = await client.mutate({
    mutation: REFRESH_EMPLOYEES_OF_BU_SKILL_STATUSES,
    variables: variables,
  })

  await client.query({
    query: BU_EMPLOYEES_AND_APPRAISALS,
    variables: { ...variables, country: Country.All },
    fetchPolicy: 'network-only',
  })

  return mutationResult
}

export const sendSkillStatusUpdateMail = async (employeeId: number): Promise<FetchResult> => {
  return await client.mutate<SendSkillStatusUpdateReminderEmailMutation, SendSkillStatusUpdateReminderEmailMutationVariables>({
    mutation: SEND_SKILL_STATUS_UPDATE_REMINDER_EMAIL,
    variables: {
      employeeId,
    },
  })
}
